import React, { ReactNode } from "react";
import Container from "@amzn/awsui-components-react/polaris/container";
import Header from "@amzn/awsui-components-react/polaris/header";
import { ColumnLayout } from "@amzn/awsui-components-react";
import StatusIndicator from "@amzn/awsui-components-react/polaris/status-indicator";
import { IReadOnlyComponentProps } from "external/programs/migration-acceleration-program/2024/fund-request/types/SchemaTypes";
import { display } from "shared/util/common/display";
import SpaceBetween from "@amzn/awsui-components-react/polaris/space-between";
import ValueWithLabel from "shared/programs/migration-acceleration-program/2024/fund-request/components/fields/ValueWithLabel";
import useFeatureFlag from "../../../../../../../util/hooks/useFeatureFlag";
import { FEATURE_FLAG_ENUMS } from "../../../../../../../util/constants/featureFlagValues";
import { MAP2024_MIGRATION_PHASE } from "external/programs/migration-acceleration-program/2024/fund-request/components/create/eligibility/util";
import {
  hasValidMigrationTrackingFields,
  isMobilizeMapLarge,
} from "../../../utils/helper";

// 3P Bedrock speed-bump outcome values (mirror of the backend enum).
const BEDROCK_SPEED_BUMP_OUTCOME_CONFIRMED_NO_3P = "CONFIRMED_NO_3P";
const BEDROCK_SPEED_BUMP_OUTCOME_UPDATED = "UPDATED";

// UTC "Month D, YYYY, HH:MM (UTC)" formatter, local to this component. The shared
// absoluteHumanReadableDateFormat emits an unbalanced "(UTC" and a stray
// console.table, so it is not reused here.
const formatUtcDateTime = (value: string): string => {
  const d = new Date(value);
  const datePart = d.toLocaleString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
  const timePart = d.toLocaleTimeString("en-US", {
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "UTC",
  });
  return `${datePart}, ${timePart} UTC`;
};

const ReadOnlyMigrationEligibility = ({
  data,
  actions = <></>,
}: IReadOnlyComponentProps) => {
  const vmwareEnabledFlag = useFeatureFlag(
    FEATURE_FLAG_ENUMS.VMWARE_MAP_ENABLED
  );
  const vmwareMSPEnabledFlag = useFeatureFlag(
    FEATURE_FLAG_ENUMS.VMWARE_BONUS_MSP_OPTION_ENABLED
  );
  const isCCSMapClaimUnlockEnabled = useFeatureFlag(
    FEATURE_FLAG_ENUMS.CCS_MAP_CLAIM_UNLOCK
  );
  const bedrockArrEnabled = useFeatureFlag(
    FEATURE_FLAG_ENUMS.ENABLE_BEDROCK_ARR
  );

  const currencyLabel = data?.isEuropeanSovereignCloud ? "EUR" : "USD";

  const definitions: {
    label: string;
    key: string;
    defaultValue?: string;
    format?: (value: unknown) => string | ReactNode;
  }[] = [
    {
      label: "Migration phase",
      key: "migrationPhase",
    },
    {
      label: `Annual recurring revenue (ARR) (${currencyLabel})`,
      key: "arrUsdAmount",
    },
    {
      label: `Migration eligible ARR (${currencyLabel})`,
      key: "eligibleArrUsdAmount",
    },
  ];

  if (data?.modernizationIncentiveEligible) {
    definitions.push({
      label: `Modernization ARR (${currencyLabel})`,
      key: "modernizationArrUsdAmount",
    });
  }

  if (
    vmwareEnabledFlag &&
    data?.migrationPhase === MAP2024_MIGRATION_PHASE.MOBILIZE
  ) {
    definitions.push({
      label: "VMware acknowledgement",
      key: "vmwareAcknowledgement",
      defaultValue: "No",
      format: (value) => (value ? "Yes" : "No"),
    });
  }

  if (vmwareMSPEnabledFlag && data?.vmwareAcknowledgement) {
    definitions.push({
      label: "VMware Manage acknowledgement",
      key: "vmwareMSPAcknowledgement",
      defaultValue: "No",
      format: (value) => (value ? "Yes" : "No"),
    });
  }
  if (
    isCCSMapClaimUnlockEnabled &&
    !!isMobilizeMapLarge(data) &&
    hasValidMigrationTrackingFields(data)
  ) {
    definitions.push(
      {
        label: "Migration ramp indicator",
        key: "migrationRampIndicator",
      },
      {
        label: "Migration health indicator",
        key: "migrationHealthIndicator",
      },
      {
        label: "Agreement Id",
        key: "agreementId",
      }
    );
  }

  if (bedrockArrEnabled) {
    // 1. 3P Bedrock model confirmation -> Confirmed / Not confirmed
    definitions.push({
      label: "3P Bedrock model confirmation",
      key: "thirdPartyBedrockAcknowledged",
      format: (value) => (
        <StatusIndicator type={value ? "success" : "stopped"}>
          {value ? "Confirmed" : "Not confirmed"}
        </StatusIndicator>
      ),
    });

    // 2. 3P Bedrock ARR -> actual amount, or "Not applicable"
    definitions.push({
      label: `3P Bedrock ARR (${currencyLabel})`,
      key: "thirdPartyBedrockArr",
      defaultValue: "Not applicable",
    });

    // 3. Speed bump response -> "No 3P models confirmed on <ts>" / "Updated on <ts>";
    //    hidden entirely when the speed bump was not shown.
    if (data?.thirdPartyBedrockSpeedBumpShown) {
      definitions.push({
        label: "3P Bedrock speed bump response",
        key: "thirdPartyBedrockSpeedBumpConfirmedAt",
        format: (value) => {
          const when = value ? ` on ${formatUtcDateTime(value as string)}` : "";
          const outcome = data?.thirdPartyBedrockSpeedBumpOutcome;
          if (outcome === BEDROCK_SPEED_BUMP_OUTCOME_CONFIRMED_NO_3P) {
            return `No 3P models confirmed${when}`;
          }
          if (outcome === BEDROCK_SPEED_BUMP_OUTCOME_UPDATED) {
            return `Updated${when}`;
          }
          return "-";
        },
      });
    }

    // 4-6. Carve-out amounts.
    definitions.push(
      {
        label: `AWS-native MAP included services ARR (${currencyLabel})`,
        key: "mapIncludedServicesArr",
      },
      {
        label: `Eligible 3P Bedrock ARR (${currencyLabel})`,
        key: "eligibleThirdPartyBedrockArr",
      },
      {
        label: `Total MAP funded ARR (${currencyLabel})`,
        key: "totalArrForIncentive",
      }
    );
  }

  if (data?.isEuropeanSovereignCloud !== undefined) {
    definitions.push({
      label: "Cloud Region",
      key: "isEuropeanSovereignCloud",
      format: (value: unknown) =>
        value ? "European Sovereign Cloud (ESC)" : "Commercial",
    });
  }

  return (
    <Container
      header={
        <Header variant="h2" actions={actions}>
          Migration details
        </Header>
      }
    >
      <ColumnLayout columns={3} variant="text-grid">
        {definitions.map(({ label, key, defaultValue, format }) => {
          return (
            <SpaceBetween key={key} size="l">
              <ValueWithLabel label={label}>
                {display({ data, key, defaultValue, format })}
              </ValueWithLabel>
            </SpaceBetween>
          );
        })}
      </ColumnLayout>
    </Container>
  );
};

export default ReadOnlyMigrationEligibility;
